import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Combo, PopulatedCombo } from "../../types";
import { FRONTEND_BASE_URL, ROUTES } from "../../lib/constants";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import toast from "react-hot-toast";
import { faCopy, faTrash, faPlus, faList, faUsers } from "@fortawesome/free-solid-svg-icons";
import { faEye, faEdit } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useOutsideClick } from "../../lib/hooks";
import { addComboReferralCode, deleteComboReferralCode } from "../../lib/apiClient";

interface ComboListItemProps {
  combo: PopulatedCombo | Combo;
  onUpdateCombo?: (id: string, updater: (c: Combo) => Combo) => void;
}

const ComboListItem: React.FC<ComboListItemProps> = ({ combo, onUpdateCombo }) => {
  const isActive = (combo as any).is_active ?? true;
  const [isCopying, setIsCopying] = useState(false);
  const [isRefsOpen, setIsRefsOpen] = useState(false);
  const refsContainerRef = useRef<HTMLDivElement>(null);

  const [referralCodes, setReferralCodes] = useState(Array.isArray(combo.refcodes) ? combo.refcodes : [])
  const publicUrlForCode = (code: string) => `${FRONTEND_BASE_URL}${ROUTES.PUBLIC_COMBO_PLANS(code)}`;

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Fallback copy failed:", err);
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopyLink = useCallback(async (link: string | null) => {
    if (!link) {
      toast.error("Combo link not available.");
      return;
    }

    setIsCopying(true);
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(link);
      } else {
        fallbackCopy(link);
      }
      toast.success("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    } finally {
      setTimeout(() => setIsCopying(false), 500);
    }
  }, []);

  const handleAddRefcode = async () => {
    try {
      const code = await addComboReferralCode(combo._id);
      toast.success("Referral code added successfully!");

      setReferralCodes((prev) => [...prev, code]); // ✅ new array, triggers re-render
    } catch (err) {
      console.error("Failed to add referral code:", err);
      toast.error("Failed to add referral code. Please try again.");
    }
  };


  const handleDeleteRefcode = async (code: string) => {
    try {
      if (referralCodes.length === 1) {
        throw new Error("At least 1 referral code is required.");
      }

      await deleteComboReferralCode(combo._id, code);
      setReferralCodes((prev) => prev.filter((c) => c !== code));

      toast.success("Referral code deleted successfully!");
    } catch (err: any) {
      console.error("Failed to delete referral code:", err);

      // Check if it was our custom validation error
      if (err instanceof Error && err.message.includes("At least 1 referral code is required")) {
        toast.error(err.message);
      } else {
        toast.error("Failed to delete referral code. It might be linked with subscriptions.");
      }
    }
  };


  useOutsideClick(refsContainerRef as any, () => setIsRefsOpen(false));

  const durationDays: number | undefined = (combo as any)?.validity_days ?? (combo as any)?.validity_days;

  return (
    <div className="bg-dark-primary p-4 rounded-lg shadow hover:shadow-md transition-all duration-200 ease-in-out border border-dark-tertiary hover:border-golden-accent flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
      {/* Info */}
      <div className="flex-grow mr-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-lg font-semibold text-text-primary truncate" title={combo.name}>
            {combo.name}
          </h3>
          <Badge status={isActive ? "active" : "inactive"} size="sm" />
        </div>
        {typeof durationDays === "number" && (
          <p className="text-sm text-text-secondary mt-0.5 truncate">
            <span className="font-medium">Duration:</span> {durationDays} days
          </p>
        )}
        <p className="text-sm text-text-secondary mt-0.5 truncate">
          <span className="font-medium">Price:</span> ₹{combo.price}
        </p>
        {combo.description && (
          <p className="text-xs text-text-secondary mt-0.5 truncate" title={combo.description || undefined}>
            {combo.description}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 flex-wrap gap-2 justify-end relative" ref={refsContainerRef}>
        {/* Public dropdown button matching channel list */}
        <div className="relative">
          <Button
            onClick={() => setIsRefsOpen((v) => !v)}
            variant="secondary"
            size="sm"
            isLoading={isCopying}
            disabled={isCopying}
            title="Public links"
          >
            <span className="flex items-center gap-1">
              Public
              <FontAwesomeIcon icon={faList} />
            </span>
          </Button>
          {isRefsOpen && (
            <div className="absolute left-0 mt-1 w-full max-w-[95vw] min-w-[180px] sm:left-1/2 sm:-translate-x-1/2 md:left-auto md:right-0 md:translate-x-0 md:w-64 md:max-w-xs bg-dark-secondary border border-dark-tertiary rounded shadow-lg p-3 flex flex-col gap-2 sm:gap-0 z-50"
            >
              {referralCodes.length === 0 ? (
                <p className="text-xs text-text-secondary italic">No links yet</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {referralCodes.map((code) => (
                    <div key={code} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                      <span className="text-xs font-mono bg-dark-tertiary px-2 py-0.5 rounded-full truncate max-w-full sm:max-w-[9rem] break-all" title={code}>
                        {code}
                      </span>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCopyLink(publicUrlForCode(code))}
                          isLoading={isCopying}
                          disabled={isCopying}
                          title="Copy Public Combo Link"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          title="Delete"
                          onClick={() => handleDeleteRefcode(code)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new referral code */}
              <div
                className="flex items-center justify-center px-3 py-2 text-green-400 hover:text-green-600 hover:bg-dark-tertiary cursor-pointer"
                onClick={handleAddRefcode}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add New Link
              </div>
            </div>
          )}
        </div>

        <Link to={ROUTES.ADMIN_COMBO_OVERVIEW(combo._id)}>
          <Button variant="secondary" size="sm" title="View Combo Overview">
            <FontAwesomeIcon icon={faEye} className="mr-1" />
            Overview
          </Button>
        </Link>
        <Link to={ROUTES.ADMIN_COMBO_SUBS(combo._id)}>
          <Button variant="secondary" size="sm" title="View Combo Subscriptions">
            <FontAwesomeIcon icon={faUsers} className="mr-1" />
            Subs
          </Button>
        </Link>
        <Link to={ROUTES.ADMIN_COMBO_EDIT(combo._id)}>
          <Button variant="info" size="sm" title="Edit Combo">
            <FontAwesomeIcon icon={faEdit} className="mr-1" />
            Edit
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default ComboListItem;
